import React, { useState, useEffect, useRef } from 'react';
import { seoServices } from '../../../services/seoAPIServices'; 
import { Input } from '../../ui/input';
import { Loader2, Link2 } from 'lucide-react';

const SeoPageSearch = ({ onSelect, placeholder = "Search for a page...", initialValue = null }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  // Use a separate state for the input display value vs the actual search query
  const [displayValue, setDisplayValue] = useState('');
  
  const wrapperRef = useRef(null);

  // Initialize display value from prop (for Edit mode)
  useEffect(() => {
      if (initialValue) {
          setDisplayValue(initialValue);
      }
  }, [initialValue]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowResults(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search Logic (Debounced)
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (query.length < 2) return;
      
      setLoading(true);
      try {
        // Ideally, your backend listPages should support a 'q' or 'slug_search' param.
        // If not, this might return everything, which isn't ideal for scale.
        // Assuming we send query to backend or filter locally if list is small.
        // For now, let's assume we pass a param 'slug_filter' or similar if your backend supports it.
        // If your backend listPages uses 'keyword' or 'q', use that.
        
        // Example: Searching by slug usually matches best
        // Note: You might need to update your listPages service/backend to accept a search string if not present.
        const res = await seoServices.listPages({ limit: 50, page: 1 }); 
        
        // Client-side filtering as a fallback if backend search isn't strict
        const filtered = res.items.filter(p => p.slug.includes(query));
        setResults(filtered);
      } catch (error) {
        console.error("Search failed", error);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  const handleSelect = (page) => {
    setDisplayValue(page.slug);
    setQuery('');
    setShowResults(false);
    onSelect(page.id); // Pass the ID back to the form
  };

  const handleInputChange = (e) => {
      const val = e.target.value;
      setDisplayValue(val);
      setQuery(val);
      setShowResults(true);
      if (val === '') onSelect(null); // Clear selection
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="relative">
        <Input 
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => { if(displayValue) setQuery(displayValue); setShowResults(true); }}
          placeholder={placeholder}
          className="pl-9 h-9 text-sm"
        />
        <Link2 className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        {loading && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" />}
      </div>

      {showResults && (
        <ul className="absolute z-50 w-full bg-white border rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
          {results.length > 0 ? (
            results.map((page) => (
              <li 
                key={page.id}
                onClick={() => handleSelect(page)}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-xs border-b last:border-none flex flex-col"
              >
                <span className="font-medium text-gray-800 truncate" title={page.slug}>{page.slug}</span>
                <span className="text-[10px] text-gray-500">{page.page_type} • {page.language_code}</span>
              </li>
            ))
          ) : (
             <li className="p-3 text-xs text-gray-500 text-center">
                 {loading ? "Searching..." : "No pages found."}
             </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default SeoPageSearch;